import { type BrandingLogoTypes } from '../types';

export const ensureDataUrl = (b64?: string, fallbackMime = 'image/png') => {
  if (!b64) {
    return '';
  }

  if (b64.startsWith('data:')) {
    return b64;
  }

  // Detect PNG vs SVG
  let mime: string;

  if (b64.startsWith('iVBORw0KGgo')) {
    mime = 'image/png';
  } else if (b64.startsWith('PHN2Zy') || b64.startsWith('PD94')) {
    mime = 'image/svg+xml';
  } else {
    mime = fallbackMime;
  }

  return `data:${mime};base64,${b64}`;
};

export const stripDataUrlPrefix = (s: string) =>
  s.replace(/^data:.*;base64,/, '');

export function inferMimeFromName(
  name: string,
  fallback = 'image/png'
): string {
  const ext = name.toLowerCase().split('.').pop() ?? '';

  if (ext === 'png') {
    return 'image/png';
  }

  if (ext === 'jpg' || ext === 'jpeg') {
    return 'image/jpeg';
  }

  if (ext === 'svg') {
    return 'image/svg+xml';
  }

  if (ext === 'gif') {
    return 'image/gif';
  }

  return fallback;
}

export function base64OrDataUrlToFile(
  input: string,
  fileName: string,
  fallbackType?: string
): File {
  let mime = fallbackType ?? inferMimeFromName(fileName);
  let b64 = input;

  // If it's a data URL, extract mime + payload
  if (input.startsWith('data:')) {
    const [meta, payload] = input.split(',');
    const [, detectedMime] = /data:(.*?);base64/.exec(meta) ?? [];

    mime = detectedMime;
    b64 = payload;
  }

  const binary = atob(b64);
  const len = binary.length;
  const bytes = new Uint8Array(len);

  for (let i = 0; i < len; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new File([bytes], fileName || 'logo', { type: mime });
}

export function toBackendImageType(
  fileOrMime: File | string,
  fileName?: string,
  fallback: BrandingLogoTypes = 'png'
): BrandingLogoTypes {
  let mime = '';
  let name = fileName ?? '';

  if (typeof fileOrMime === 'string') {
    mime = fileOrMime.toLowerCase();
  } else {
    mime = (fileOrMime.type || '').toLowerCase();
    name = fileOrMime.name || name;
  }

  if (mime.includes('svg')) {
    return 'svg';
  }

  if (mime.includes('png')) {
    return 'png';
  }

  // Fallback to filename extension if MIME is missing/empty
  const ext = name.toLowerCase().split('.').pop() ?? '';

  if (ext === 'svg') {
    return 'svg';
  }

  if (ext === 'png') {
    return 'png';
  }

  // Optional: if a data URL string was passed
  if (typeof fileOrMime === 'string' && fileOrMime.startsWith('data:')) {
    const detected = /data:(.*?);base64/.exec(fileOrMime)?.[1] ?? '';

    if (detected.includes('svg')) {
      return 'svg';
    }

    if (detected.includes('png')) {
      return 'png';
    }
  }

  return fallback;
}
